import { enableProdMode } from '@angular/core';
import { AppModule } from './app/app.module';
import { environment } from './environments/environment';
import { getClient, init, lazyLoadIntegration } from '@sentry/angular';
import { platformBrowser } from '@angular/platform-browser';
import { isNativeShell } from './app/core/platform/platform.service';
import { CapacitorUpdater } from '@capgo/capacitor-updater';

// Confirm this bundle started successfully so Capgo can keep it instead of rolling back to the previous bundle.
CapacitorUpdater.notifyAppReady();

const release = <HTMLMetaElement>document.getElementsByName('app-version')[0];

init({
  dsn: environment.sentry ?? undefined,
  release: release.content,
  environment: environment.sentryEnvironment,
  ignoreErrors: [
    'Non-Error exception captured',
    'Non-Error promise rejection captured',
    // Google Maps vector rendering can fail when the browser cannot create a WebGL context.
    /^Could not find a 3d context, error: \d+$/,
    // https://github.com/getsentry/sentry-javascript/issues/2292,  https://github.com/getsentry/sentry-javascript/issues/3440
    `${environment.tigerService}`,
  ],
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
});

// Replay pulls in ~250 kB of rrweb. `lazyLoadIntegration` fetches it from the
// Sentry CDN after boot instead, so it never lands in the initial bundle. The
// first moments of a session go unrecorded, and a blocked CDN just means no
// replay — neither is worth blocking startup for, so this stays fire-and-forget.
if (environment.production) {
  lazyLoadIntegration('replayIntegration')
    .then((replayIntegration) =>
      getClient()?.addIntegration(replayIntegration()),
    )
    .catch((error) =>
      console.error('Sentry Replay integration failed to load:', error),
    );
}

if (!isNativeShell() && 'serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/service-worker.js', { updateViaCache: 'none' })
      .then((reg) => console.log('Service Worker registered:', reg))
      .catch((err) =>
        console.error('Service Worker registration failed:', err),
      );
  });
}

if (environment.production) {
  enableProdMode();
}
if (!/localhost/.test(document.location.host)) {
  enableProdMode();
}

const loadingElement = document.querySelector<HTMLElement>('.app-loading');

// Must match the .app-loading opacity transition in index.html.
const SPLASH_FADE_MS = 300;

function hideSplashScreen(): void {
  if (!loadingElement) return;

  loadingElement.classList.add('loaded');

  // A timer rather than transitionend: transitions do not progress while the
  // tab is hidden, so waiting on the event would leave this fixed
  // z-index-10001 overlay covering the app until the tab is next painted.
  setTimeout(() => loadingElement.remove(), SPLASH_FADE_MS);
}

platformBrowser().bootstrapModule(AppModule).then(hideSplashScreen);
